import {useEffect, useMemo, useState} from 'react';
import {useForm, useFieldArray, useWatch} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useNavigate} from 'react-router-dom';
import {ArrowLeft, ArrowRight, Plus, X} from 'lucide-react';
import {Form} from '@common-web/components/ui/form';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@common-web/components/ui/tooltip';
import {useEnrollSteps} from '@client/pages/public/enroll/enrollStepsContext';
import {useEnrollPreview} from '@client/pages/public/enroll/enrollPreview';
import {EnrollEvent, useEnrollAnalytics} from '@client/pages/public/enroll/enrollAnalytics';
import {STEP} from '@client/pages/public/enroll/enrollModel';
import type {EnrollStep} from '@client/pages/public/enroll/enrollModel';
import {
  BENEFICIARY_RELATIONSHIP_OPTIONS,
  buildCheckoutSchema,
  EMPTY_DEPENDENT,
  RELATIONSHIP_OPTIONS,
  GENDER_OPTIONS,
} from '@client/pages/public/enroll/checkout/checkoutSchema';
import type {CheckoutValues} from '@client/pages/public/enroll/checkout/checkoutSchema';
import {TextField} from '@client/pages/public/enroll/checkout/fields/TextField';
import {DateField} from '@client/pages/public/enroll/checkout/fields/DateField';
import {CheckboxField} from '@client/pages/public/enroll/checkout/fields/CheckboxField';
import {StateSelect} from '@client/pages/public/enroll/checkout/fields/StateSelect';
import {SelectField} from '@client/pages/public/enroll/checkout/fields/SelectField';
import {RadioField} from '@client/pages/public/enroll/checkout/fields/RadioField';
import {PlanNotices} from '@client/pages/public/enroll/checkout/PlanNotices';
import {ConfirmEnrollmentDialog} from '@client/pages/public/enroll/checkout/ConfirmEnrollmentDialog';
import {analyzeCoverage} from '@client/pages/public/enroll/checkout/coverageChecks';
import {CoverageWarnings} from '@client/pages/public/enroll/checkout/CoverageWarnings';
import {CheckoutDevTools} from '@client/pages/public/enroll/checkout/CheckoutDevTools';

const GENDER_DESCRIPTION = 'As assigned at birth.';

export function CheckoutStep() {
  const {
    group,
    tiers,
    selections,
    applicant,
    dependents,
    beneficiary,
    beneficiaryRequired,
    setApplicant,
    setDependents,
    setBeneficiary,
    clearEnrollmentReceipt,
  } = useEnrollSteps();
  const {isPreview} = useEnrollPreview();
  const {track} = useEnrollAnalytics();
  const navigate = useNavigate();
  const base = `/${group.slug}`;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set());

  useEffect(() => {
    track(EnrollEvent.CheckoutViewed);
  }, [track]);

  const checkoutConfig = group.checkout;
  const schema = useMemo(
    () => buildCheckoutSchema({beneficiary: beneficiaryRequired}),
    [beneficiaryRequired],
  );

  const form = useForm<CheckoutValues>({
    resolver: zodResolver(schema),
    defaultValues: {...applicant, dependents, beneficiary, smsConsent: false},
  });

  const {fields, append, remove} = useFieldArray({control: form.control, name: 'dependents'});

  // Live dependents drive the coverage check as the user edits, so a resolved mismatch clears itself.
  const watchedDependents = useWatch({control: form.control, name: 'dependents'});
  const issues = useMemo(
    () => analyzeCoverage(group, tiers, selections, watchedDependents ?? []),
    [group, tiers, selections, watchedDependents],
  );

  // Acknowledgeable issues clear once their box is checked; must-resolve issues only clear when fixed.
  const unresolvedIssues = issues.filter(
    (issue) => !issue.acknowledgeable || !acknowledged.has(issue.id),
  );
  const hasFieldErrors = Object.keys(form.formState.errors).length > 0;
  const submitDisabled = submitAttempted && (unresolvedIssues.length > 0 || hasFieldErrors);

  function toggleAck(id: string) {
    setAcknowledged((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // Valid submit + no unresolved coverage issues opens the confirmation gate; the commit happens on
  // Agree & submit. Coverage issues keep the confirm dialog closed (the warning section handles them).
  function onValid() {
    if (unresolvedIssues.length > 0) return;
    setConfirmOpen(true);
  }

  // Persist the current form values to context so leaving the step (Back, Edit coverage, or Confirm)
  // keeps the draft. smsConsent is validated at submit but isn't part of the applicant; recording it
  // is a server concern handled when the real submission API is wired in.
  function persistDraft() {
    const {
      dependents: nextDependents,
      beneficiary: nextBeneficiary,
      smsConsent,
      ...personal
    } = form.getValues();
    void smsConsent;
    setApplicant(personal);
    setDependents(nextDependents);
    setBeneficiary(nextBeneficiary);
  }

  function handleEditCoverage(step: EnrollStep) {
    persistDraft();
    navigate(`${base}/${step}`);
  }

  // Advance — the confirmation step runs the submission + receipt. Clear any prior receipt so the
  // confirmation step shows its loading state and re-submits.
  function handleConfirm() {
    persistDraft();
    clearEnrollmentReceipt();
    setConfirmOpen(false);
    navigate(`${base}/${STEP.Confirmation}`);
  }

  const submitButton = (
    <button
      type='submit'
      disabled={submitDisabled}
      onClick={() => setSubmitAttempted(true)}
      className='inline-flex h-11 items-center gap-2 rounded-lg bg-[var(--enroll-accent)] px-5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[var(--enroll-accent-hover)] disabled:pointer-events-none disabled:opacity-40'
    >
      Submit enrollment
      <ArrowRight className='h-4 w-4' />
    </button>
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onValid)} className='relative space-y-6' noValidate>
        {(import.meta.env.DEV || isPreview) && <CheckoutDevTools form={form} />}

        <div className='space-y-2'>
          <h1 className='text-3xl font-bold text-[var(--enroll-ink)]'>Checkout</h1>
          <p className='max-w-2xl text-neutral-600'>
            Enter your information and any dependents you're enrolling.
          </p>
        </div>

        <section className='rounded-2xl border border-neutral-200 bg-white p-6'>
          <h2 className='text-lg font-semibold text-[var(--enroll-ink)]'>Your information</h2>
          <div className='mt-5 grid gap-x-5 gap-y-4 sm:grid-cols-2'>
            <TextField name='firstName' label='First name' autoComplete='given-name' />
            <TextField name='lastName' label='Last name' autoComplete='family-name' />
            <DateField name='dob' label='Date of birth' />
            <RadioField name='gender' label='Gender' options={GENDER_OPTIONS} description={GENDER_DESCRIPTION} />
            {checkoutConfig?.collectDateOfHire && (
              <DateField
                name='dateOfHire'
                label='Date of hire'
                hint='Your date of hire helps us verify your eligibility and plan effective date.'
              />
            )}
            <TextField
              name='ssn'
              label='Social Security Number'
              placeholder='123-45-6789'
              inputMode='numeric'
            />
            <TextField
              name='email'
              label='Personal email'
              type='email'
              autoComplete='email'
              inputMode='email'
              hint="Please use a personal email — not a work address. It's how we reach you about your coverage, ID card, and any healthcare needs, even if you change jobs."
            />
            <TextField
              name='phone'
              label={checkoutConfig?.phoneLabel ?? 'Phone'}
              type='tel'
              autoComplete='tel'
              inputMode='tel'
              className={checkoutConfig?.smsConsentDisclosure ? 'sm:col-span-2' : undefined}
            />
            {checkoutConfig?.smsConsentDisclosure && (
              <CheckboxField
                name='smsConsent'
                label={checkoutConfig.smsConsentDisclosure}
                className='sm:col-span-2'
              />
            )}
            <TextField
              name='address1'
              label='Address line 1'
              autoComplete='address-line1'
              className='sm:col-span-2'
            />
            <TextField
              name='address2'
              label='Address line 2 (optional)'
              autoComplete='address-line2'
              className='sm:col-span-2'
            />
            <TextField name='city' label='City' autoComplete='address-level2' />
            <StateSelect name='state' label='State' />
            <TextField name='zip' label='ZIP' inputMode='numeric' autoComplete='postal-code' />
          </div>
        </section>

        <DependentsSection
          fields={fields}
          onAdd={() => append(EMPTY_DEPENDENT)}
          onRemove={remove}
        />

        {beneficiaryRequired && <BeneficiarySection />}

        <PlanNotices />

        {submitAttempted && (
          <CoverageWarnings
            issues={issues}
            acknowledged={acknowledged}
            onToggleAck={toggleAck}
            onEditCoverage={handleEditCoverage}
            onAddDependent={() => append(EMPTY_DEPENDENT)}
            onRemoveDependent={remove}
            onRemoveAllDependents={() => remove()}
          />
        )}

        <div className='flex items-center justify-between border-t border-neutral-200 pt-6'>
          <button
            type='button'
            onClick={() => {
              persistDraft();
              navigate(`${base}/${STEP.Review}`);
            }}
            className='inline-flex h-11 items-center gap-2 rounded-lg border border-neutral-300 px-4 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100'
          >
            <ArrowLeft className='h-4 w-4' />
            Back
          </button>
          {submitDisabled ? (
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex cursor-not-allowed'>{submitButton}</span>
                </TooltipTrigger>
                <TooltipContent className='border border-amber-300 bg-amber-50 font-medium text-amber-900'>
                  {unresolvedIssues.length > 0
                    ? 'Resolve the highlighted items above to continue.'
                    : 'Fix the highlighted fields to continue.'}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            submitButton
          )}
        </div>
      </form>

      <ConfirmEnrollmentDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={handleConfirm}
      />
    </Form>
  );
}

function DependentsSection({
  fields,
  onAdd,
  onRemove,
}: {
  fields: {id: string}[];
  onAdd: () => void;
  onRemove: (index: number) => void;
}) {
  return (
    <section className='rounded-2xl border border-neutral-200 bg-white p-6'>
      <div className='flex items-center justify-between'>
        <h2 className='text-lg font-semibold text-[var(--enroll-ink)]'>Dependents</h2>
        <button
          type='button'
          onClick={onAdd}
          className='inline-flex h-9 items-center gap-1.5 rounded-lg border border-neutral-300 px-3 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100'
        >
          <Plus className='h-4 w-4' />
          Add dependent
        </button>
      </div>

      {fields.length === 0 ? (
        <p className='mt-3 text-sm text-neutral-500'>
          No dependents added. Add a spouse or child if you're enrolling them.
        </p>
      ) : (
        <div className='mt-5 space-y-5'>
          {fields.map((field, index) => (
            <div key={field.id} className='relative rounded-xl border border-neutral-200 p-5'>
              <button
                type='button'
                onClick={() => onRemove(index)}
                aria-label={`Remove dependent ${index + 1}`}
                className='absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700'
              >
                <X className='h-4 w-4' />
              </button>
              <div className='grid gap-x-5 gap-y-4 pr-8 sm:grid-cols-2'>
                <TextField name={`dependents.${index}.firstName`} label='First name' />
                <TextField name={`dependents.${index}.lastName`} label='Last name' />
                <SelectField
                  name={`dependents.${index}.relationship`}
                  label='Relationship'
                  options={RELATIONSHIP_OPTIONS}
                />
                <DateField name={`dependents.${index}.dob`} label='Date of birth' />
                <RadioField
                  name={`dependents.${index}.gender`}
                  label='Gender'
                  options={GENDER_OPTIONS}
                  description={GENDER_DESCRIPTION}
                />
                <TextField
                  name={`dependents.${index}.ssn`}
                  label='Social Security Number'
                  placeholder='123-45-6789'
                  inputMode='numeric'
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function BeneficiarySection() {
  return (
    <section className='rounded-2xl border border-neutral-200 bg-white p-6'>
      <h2 className='text-lg font-semibold text-[var(--enroll-ink)]'>Beneficiary</h2>
      <p className='mt-1 text-sm text-neutral-500'>
        The person who receives the life insurance benefit.
      </p>
      <div className='mt-5 grid gap-x-5 gap-y-4 sm:grid-cols-2'>
        <TextField name='beneficiary.firstName' label='First name' />
        <TextField name='beneficiary.lastName' label='Last name' />
        <SelectField
          name='beneficiary.relationship'
          label='Relationship'
          options={BENEFICIARY_RELATIONSHIP_OPTIONS}
        />
        <DateField name='beneficiary.dob' label='Date of birth' />
        <TextField
          name='beneficiary.phone'
          label='Phone'
          type='tel'
          inputMode='tel'
          className='sm:col-span-2'
        />
        <TextField name='beneficiary.address' label='Address' className='sm:col-span-2' />
        <TextField name='beneficiary.city' label='City' />
        <StateSelect name='beneficiary.state' label='State' />
        <TextField name='beneficiary.zip' label='ZIP' inputMode='numeric' />
      </div>
    </section>
  );
}
